import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Printer, AlertTriangle, Layers, Settings2 } from "lucide-react";
import { usePrintChunkSize } from "@/lib/printSettings";
import { listDevices, setSelectedDevice, clearSelectedDevice } from "@/lib/zebra";
import { resolveDeviceForMedia, printersForMedia, printerDisplayName, MEDIA_LABEL, hydratePrinterConfig } from "@/lib/printerMap";

// Universal print guard: NOTHING prints until the operator sees the exact total
// and deliberately confirms, and the confirm button is a ONE-FIRE LOCK (disables
// on click and stays locked until the print finishes) so a double-click can never
// fire the same run twice. Wrap any print body:
//   confirmAndRun({ labelCount, copies, alreadyPrinted }, async () => { ...print... })
export function usePrintConfirm() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState(null);
  const actionRef = useRef(null);
  const [chunkSize, setChunkSize] = usePrintChunkSize();
  // Local draft so the field shows EXACTLY what the operator types; clamp/persist only
  // on blur or Enter (fixes the "type 50 → shows 600" clamp-on-every-keystroke bug).
  const [chunkDraft, setChunkDraft] = useState(String(chunkSize));
  useEffect(() => { setChunkDraft(String(chunkSize)); }, [chunkSize, open]);
  const commitChunk = () => { const v = setChunkSize(chunkDraft); setChunkDraft(String(v)); };

  // Printer routing: detect connected printers when the dialog opens and default to the
  // printer holding the chosen media. The override list is restricted to printers that
  // hold that same stock, so a mismatched layout/printer pair can't be selected at all.
  const [devices, setDevices] = useState([]);
  const [selUid, setSelUid] = useState("");
  const [showPicker, setShowPicker] = useState(false);
  useEffect(() => {
    if (!open) { setShowPicker(false); return; }
    let alive = true;
    hydratePrinterConfig();
    listDevices().then((list) => {
      if (!alive) return;
      setDevices(list);
      const auto = meta?.mediaType ? resolveDeviceForMedia(meta.mediaType, list) : null;
      // NO list[0] guess: if nothing holds this stock, leave the pick EMPTY so the job
      // refuses — never silently route a layout onto whatever stock happens to be loaded.
      const pick = auto?.uid || "";
      setSelUid(pick);
    }).catch(() => { if (alive) { setDevices([]); setSelUid(""); } });
    return () => { alive = false; };
  }, [open, meta]);

  const eligible = meta?.mediaType ? printersForMedia(meta.mediaType, devices) : [];
  const hiddenCount = Math.max(0, devices.length - eligible.length);
  const stockLabel = meta?.mediaType ? (MEDIA_LABEL[meta.mediaType] || meta.mediaType) : "this media";

  const confirmAndRun = useCallback((m, action) => {
    if (busy || open) return;   // a confirm/print is already in flight → ignore re-clicks
    const copies = Math.max(1, m.copies || 1);
    const labelCount = Math.max(0, m.labelCount || 0);
    setMeta({
      title: m.title || "Confirm print",
      noun: m.noun || "labels",
      copies, labelCount,
      total: labelCount * copies,
      alreadyPrinted: m.alreadyPrinted || 0,
      hideSplit: !!m.hideSplit,
      note: m.note || "",
      mediaType: m.mediaType || null,
    });
    actionRef.current = action;
    setOpen(true);
  }, [busy, open]);

  const onConfirm = async () => {
    if (busy) return;           // one-fire lock: ignore any second click
    setBusy(true);
    const device = devices.find((d) => d.uid === selUid) || null;
    if (device) setSelectedDevice(device);   // route THIS job to the chosen printer
    try {
      await actionRef.current?.();
    } finally {
      clearSelectedDevice();
      setBusy(false);
      setOpen(false);
      actionRef.current = null;
    }
  };

  const onCancel = () => { if (!busy) { setOpen(false); actionRef.current = null; } };

  const printConfirmDialog = (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onCancel(); }}>
      <DialogContent className="rounded-lg border-slate-200 sm:max-w-md" data-testid="print-confirm-dialog"
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Printer className="w-5 h-5 text-[#B01E28]" />{meta?.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div data-testid="print-confirm-total" className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center">
            <div className="font-display text-4xl font-black text-[#1F1F1F] tabular-nums">{meta?.total ?? 0}</div>
            <div className="text-xs text-slate-500 mt-1">
              total {meta?.noun} = {meta?.labelCount ?? 0} × {meta?.copies ?? 1} cop{(meta?.copies ?? 1) === 1 ? "y" : "ies"}
            </div>
          </div>
          {/* Printer routing — resolved from the chosen MEDIA. The override only lists
              printers holding that same stock, so a mismatch can't be selected. */}
          <div className="rounded-lg border border-slate-200 p-3" data-testid="print-confirm-printer">
            <div className="flex items-center gap-2 text-sm">
              <Printer className="w-4 h-4 text-slate-400 shrink-0" />
              {devices.length === 0 ? (
                <span className="text-amber-700 min-w-0" data-testid="print-confirm-no-printer">No printer detected via Browser Print. Check it's running, then reopen.</span>
              ) : selUid ? (
                <span className="text-slate-700 min-w-0 truncate">Prints to <b className="text-slate-900" data-testid="print-confirm-printer-name">{printerDisplayName(devices.find((d) => d.uid === selUid))}</b></span>
              ) : (
                <span className="text-[#B01E28] font-semibold min-w-0" data-testid="print-confirm-unmapped">No connected printer holds {stockLabel} — nothing will print. Set which printer holds it in Setup, or go back and choose different media.</span>
              )}
              <div className="ml-auto flex items-center gap-2 shrink-0">
                {eligible.length > 1 && (
                  <button type="button" data-testid="print-confirm-different-printer" disabled={busy}
                    onClick={() => setShowPicker((s) => !s)}
                    className="text-[11px] text-[#B01E28] hover:underline inline-flex items-center gap-0.5">
                    Different {stockLabel} printer <Settings2 className="w-3 h-3" />
                  </button>
                )}
                <Link to="/printer-settings" className="text-[11px] text-slate-400 hover:text-[#B01E28] inline-flex items-center gap-0.5" data-testid="print-confirm-manage-printers">
                  <Settings2 className="w-3 h-3" />Setup
                </Link>
              </div>
            </div>
            {showPicker && eligible.length > 1 && (
              <select data-testid="print-confirm-printer-select" value={selUid} disabled={busy}
                onChange={(e) => setSelUid(e.target.value)}
                className="mt-2 h-9 w-full rounded-md border border-slate-300 bg-white px-2 text-sm">
                {eligible.map((d) => (
                  <option key={d.uid} value={d.uid}>{printerDisplayName(d)}</option>
                ))}
              </select>
            )}
            {hiddenCount > 0 && (
              <div data-testid="print-confirm-excluded" className="text-[11px] text-slate-500 mt-1.5">
                {hiddenCount} other connected printer{hiddenCount === 1 ? "" : "s"} can't print this — {hiddenCount === 1 ? "it doesn't" : "they don't"} hold {stockLabel}.
              </div>
            )}
          </div>
          {meta?.alreadyPrinted > 0 && (
            <div data-testid="print-confirm-already" className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span><b>{meta.alreadyPrinted}</b> of these were already printed — reprinting makes DUPLICATE physical labels for the same numbers. One number should have one label.</span>
            </div>
          )}
          <div className="rounded-lg border border-slate-200 p-3">
            {meta?.hideSplit ? (
              <p data-testid="print-confirm-note" className="text-xs text-slate-600">{meta.note}</p>
            ) : (meta?.total ?? 0) >= chunkSize ? (
              <>
                <div className="flex items-center gap-2 text-xs">
                  <Layers className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-slate-600">Safe batch size</span>
                  <Input data-testid="print-confirm-chunk-size" type="number" min={5} max={600} value={chunkDraft} disabled={busy}
                    onChange={(e) => setChunkDraft(e.target.value)} onBlur={commitChunk}
                    onKeyDown={(e) => { if (e.key === "Enter") commitChunk(); }}
                    className="h-7 w-20 rounded-md border-slate-200 ml-auto text-center" />
                  <span className="text-slate-400">labels</span>
                </div>
                {(meta?.total ?? 0) > chunkSize ? (
                  <p data-testid="print-confirm-split" className="text-[11px] text-slate-500 mt-1.5">
                    Large run → prints in <b>{Math.ceil((meta.total) / chunkSize)}</b> sub-runs of up to <b>{chunkSize}</b>, pausing between each so the printer never silently stalls.
                  </p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-1.5">At the safe batch size — prints in one pass. Raise it once you've proven a higher reliable max on the printer.</p>
                )}
              </>
            ) : (
              <p data-testid="print-confirm-small" className="text-[11px] text-slate-400">Small run — prints in one pass. The safe-batch split + STOP kick in at <b>{chunkSize}+</b> labels.</p>
            )}
          </div>
          <p className="text-[11px] text-slate-400">This prints physical label stock. Confirm to print once. Use STOP to halt a run in progress.</p>
        </div>
        <DialogFooter>
          <Button data-testid="print-confirm-cancel" variant="outline" className="rounded-lg border-slate-200" disabled={busy} onClick={onCancel}>Cancel</Button>
          <Button data-testid="print-confirm-go" disabled={busy || (devices.length > 0 && !selUid)} onClick={onConfirm}
            className="rounded-lg bg-[#1F1F1F] hover:bg-black text-white">
            {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Printing…</> : <><Printer className="w-4 h-4 mr-1.5" />Confirm · print {meta?.total ?? 0}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { printConfirmDialog, confirmAndRun };
}
